import React from 'react';
import { LeaderboardEntry, Badge } from '../data/mockData';
import { Trophy, Medal, TrendingUp, TrendingDown, Award, Send, FileText, Flame, Star, Crown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const badgeConfig: Record<Badge, {
  icon: React.ElementType;
  color: string;
}> = {
  'Top Closer': {
    icon: Award,
    color: 'text-yellow-400'
  },
  'Submission King': {
    icon: Send,
    color: 'text-blue-400'
  },
  'Doc Machine': {
    icon: FileText,
    color: 'text-purple-400'
  },
  'Hot Streak': {
    icon: Flame,
    color: 'text-red-400'
  },
  'Rising Star': {
    icon: Star,
    color: 'text-orange-400'
  }
};
interface Props {
  entries: LeaderboardEntry[];
}
function PodiumStep({
  entry,
  rank,
  height,
  colorClass,
  icon: Icon,
  delay
}: {
  entry: LeaderboardEntry;
  rank: number;
  height: string;
  colorClass: string;
  icon: React.ElementType;
  delay: number;
}) {
  const isChampion = rank === 1;
  return <motion.div className="flex flex-col items-center flex-1 min-w-0" initial={{
    opacity: 0,
    y: 40
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: 20
  }} transition={{
    type: 'spring',
    stiffness: 260,
    damping: 22,
    delay
  }} whileHover={{
    y: -4
  }} data-id="element-80">
      {/* Champion Crown */}
      {isChampion && <motion.div initial={{
      opacity: 0,
      scale: 0,
      rotate: -20
    }} animate={{
      opacity: 1,
      scale: 1,
      rotate: 0
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 15,
      delay: delay + 0.3
    }} className="mb-1" data-id="element-81">
          <Crown className="w-7 h-7 sm:w-8 sm:h-8 text-gold drop-shadow-md" data-id="element-82" />
        </motion.div>}

      {/* Avatar & Name */}
      <div className="flex flex-col items-center text-center mb-3" data-id="element-83">
        <div className="relative mb-1.5" data-id="element-84">
          {/* Champion glow ring */}
          {isChampion && <div className="absolute -inset-1.5 rounded-full bg-gold/20 animate-pulse" data-id="element-85" />}
          {entry.avatar ? <img src={entry.avatar} alt={entry.name} className={`relative w-14 h-14 ${isChampion ? 'sm:w-20 sm:h-20' : 'sm:w-[4.5rem] sm:h-[4.5rem]'} rounded-full object-cover border-[3px] ${rank === 1 ? 'border-gold' : rank === 2 ? 'border-silver' : 'border-bronze'} shadow-lg`} data-id="element-86" /> : <div className={`relative w-14 h-14 ${isChampion ? 'sm:w-20 sm:h-20' : 'sm:w-[4.5rem] sm:h-[4.5rem]'} rounded-full bg-ez-dark text-white flex items-center justify-center font-bold text-lg sm:text-xl border-[3px] ${rank === 1 ? 'border-gold' : rank === 2 ? 'border-silver' : 'border-bronze'} shadow-lg`} data-id="element-87">
              {entry.name.charAt(0)}
            </div>}
          <div className="absolute -bottom-1.5 -right-1.5 bg-white rounded-full p-0.5 shadow-md" data-id="element-88">
            <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${colorClass}`} data-id="element-89" />
          </div>
        </div>
        <h3 className={`font-bold text-gray-900 leading-tight line-clamp-1 max-w-full px-1 ${isChampion ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`} data-id="element-90">
          {entry.name}
        </h3>
        {entry.teamName && <p className="text-[10px] sm:text-xs text-gray-400 leading-tight" data-id="element-91">
            {entry.teamName}
          </p>}
      </div>

      {/* Podium Block */}
      <div className={`w-full ${height} rounded-t-xl shadow-xl flex flex-col items-center px-3 relative overflow-hidden ${isChampion ? 'bg-gradient-to-b from-ez-dark to-slate-900' : 'bg-ez-dark'}`} data-id="element-92">
        {/* Top accent bar */}
        <div className={`absolute top-0 left-0 right-0 ${isChampion ? 'h-1.5' : 'h-1'} ${rank === 1 ? 'bg-gold' : rank === 2 ? 'bg-silver' : 'bg-bronze'}`} data-id="element-93" />

        {/* Champion glow effect */}
        {isChampion && <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-32 bg-gold/5 rounded-full blur-2xl pointer-events-none" data-id="element-94" />}

        {/* Watermark rank */}
        <div className="absolute bottom-2 text-3xl sm:text-5xl font-black text-white/[0.06] select-none pointer-events-none" data-id="element-95">
          {rank}
        </div>

        {/* Content */}
        <div className="flex flex-col items-center justify-center gap-2 sm:gap-3 pt-4 sm:pt-5 z-10 w-full" data-id="element-96">
          {/* Champion label */}
          {isChampion && <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-gold -mb-1" data-id="element-97">
              Champion
            </div>}

          {/* Score */}
          <div className="text-center" data-id="element-98">
            <div className={`text-white font-bold leading-none tracking-tight ${isChampion ? 'text-lg sm:text-2xl' : 'text-base sm:text-xl'}`} data-id="element-99">
              {entry.score.toLocaleString()}
            </div>
            <div className="text-[9px] sm:text-[10px] text-ez-green uppercase font-bold tracking-wider mt-0.5" data-id="element-100">
              Points
            </div>
          </div>

          {/* Metrics row */}
          <div className="flex items-center justify-center gap-2 sm:gap-3 w-full" data-id="element-101">
            <div className="flex flex-col items-center" data-id="element-102">
              <span className="text-white font-bold text-xs sm:text-sm leading-none" data-id="element-103">
                {entry.docOuts}
              </span>
              <span className="text-[7px] sm:text-[8px] text-gray-500 uppercase tracking-wider font-semibold mt-0.5" data-id="element-104">
                Docs
              </span>
            </div>
            <div className="w-px h-5 bg-white/10" data-id="element-105" />
            <div className="flex flex-col items-center" data-id="element-106">
              <span className="text-white font-bold text-xs sm:text-sm leading-none" data-id="element-107">
                {entry.submissions}
              </span>
              <span className="text-[7px] sm:text-[8px] text-gray-500 uppercase tracking-wider font-semibold mt-0.5" data-id="element-108">
                Subs
              </span>
            </div>
            <div className="w-px h-5 bg-white/10" data-id="element-109" />
            <div className="flex flex-col items-center" data-id="element-110">
              <span className="text-white font-bold text-xs sm:text-sm leading-none" data-id="element-111">
                {entry.ctcs}
              </span>
              <span className="text-[7px] sm:text-[8px] text-gray-500 uppercase tracking-wider font-semibold mt-0.5" data-id="element-112">
                CTCs
              </span>
            </div>
          </div>

          {/* Point delta */}
          {entry.pointDelta != null && entry.pointDelta !== 0 && <div className={`text-[10px] sm:text-xs font-bold tracking-wide flex items-center gap-0.5 ${entry.pointDelta > 0 ? 'text-ez-green' : 'text-red-400'}`} data-id="element-113">
              {entry.pointDelta > 0 ? <TrendingUp className="w-3 h-3" data-id="element-114" /> : <TrendingDown className="w-3 h-3" data-id="element-115" />}
              {entry.pointDelta > 0 ? '+' : ''}
              {entry.pointDelta}
            </div>}

          {/* Badges */}
          {entry.badges.length > 0 && <div className="flex flex-wrap justify-center gap-1" data-id="element-116">
              {entry.badges.map(badge => {
            const config = badgeConfig[badge];
            if (!config) return null;
            const BadgeIcon = config.icon;
            return <div key={badge} className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] sm:text-[9px] font-semibold bg-white/10 text-white/80" title={badge} data-id="element-117">
                    <BadgeIcon className={`w-2.5 h-2.5 sm:w-3 sm:h-3 ${config.color}`} data-id="element-118" />
                    <span className="hidden sm:inline" data-id="element-119">{badge}</span>
                  </div>;
          })}
            </div>}
        </div>
      </div>
    </motion.div>;
}
export function Podium({
  entries
}: Props) {
  if (entries.length < 3) return null;
  const first = entries[0];
  const second = entries[1];
  const third = entries[2];
  // Key for re-triggering animations on data change
  const animKey = `${first.id}-${second.id}-${third.id}`;
  return <AnimatePresence mode="wait" data-id="element-120">
      <motion.div key={animKey} className="flex items-end justify-center max-w-2xl mx-auto mb-10 mt-6 px-2 sm:px-4 gap-2 sm:gap-3" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} data-id="element-121">
        <PodiumStep entry={second} rank={2} height="h-44 sm:h-56" colorClass="text-silver" icon={Medal} delay={0.1} data-id="element-122" />
        <PodiumStep entry={first} rank={1} height="h-52 sm:h-64" colorClass="text-gold" icon={Trophy} delay={0.25} data-id="element-123" />
        <PodiumStep entry={third} rank={3} height="h-36 sm:h-48" colorClass="text-bronze" icon={Medal} delay={0.05} data-id="element-124" />
      </motion.div>
    </AnimatePresence>;
}